/**
 * Browser panel (bots B2, 2026-09-03) — watch an agent's screen work, take
 * it over, hand it back. One panel per screen; with no screen the panel is
 * a picker over the open screens plus a URL box that opens one (the human
 * twin of the agent's browser_read: POST /api/browser/read, same engine,
 * same gates, same audit row).
 *
 * During a take-over the frame becomes an input surface: clicks, wheel and
 * keys are forwarded to the real page (never recorded); the agent is
 * blinded until Resume. Frames only flow while this panel is open — the
 * server captures nothing without a viewer.
 */
import { Check, Globe, Hand, Play, Plus, RefreshCw, ShieldAlert, Square, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { friendlyStatus } from "../../../../lib/api";
import { useDockStore } from "../../../../stores/dock";
import { toast } from "../../../../stores/toast";
import { Button } from "../../../common/Button";
import type { DockKindProps } from "../../registry";
import styles from "./BrowserPanel.module.css";
import { SPECIAL_KEYS, type ScreenRow, browserData, browserPanel, frameSrc, handoffLabel, statusLabel, toViewportPoint } from "./data";
import { RecordSection } from "./RecordSection";
import { useBrowserSocket } from "./useBrowserSocket";

async function readDetail(res: Response): Promise<string> {
	// Text first: the contract middleware refuses in plain prose ("Contract
	// blocked: … browser_read"), and a status code is not an explanation.
	const text = await res.text().catch(() => "");
	try {
		const body = JSON.parse(text);
		const d = body?.detail ?? body?.message;
		if (typeof d === "string") return d;
		if (d && typeof d === "object") return String(d.message ?? d.error ?? JSON.stringify(d));
	} catch { /* not json */ }
	return text.trim() || friendlyStatus(res.status);
}

function Picker({ panel }: DockKindProps) {
	const { t } = useTranslation();
	const [screens, setScreens] = useState<ScreenRow[] | null>(null);
	const [owner, setOwner] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [url, setUrl] = useState("");
	const [busy, setBusy] = useState(false);
	const openPanel = useDockStore((s) => s.openPanel);

	const load = useCallback(async () => {
		setError(null);
		try {
			const res = await fetch("/api/browser/screens");
			if (!res.ok) throw new Error(await readDetail(res));
			const body = await res.json();
			setScreens(body.screens ?? []);
			setOwner(body.owner !== false);
		} catch (e) {
			setScreens([]);
			setError(String((e as Error).message ?? e));
		}
	}, []);

	useEffect(() => { void load(); }, [load]);

	const openScreen = (id: string, title: string) => {
		openPanel({ ...panel, id: `browser:${id}`, title: `Screen · ${title || id}`, data: { screenId: id } });
	};

	const openUrl = async () => {
		const target = url.trim();
		if (busy) return;
		setBusy(true);
		try {
			const res = target
				? await fetch("/api/browser/read", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ url: target, agent_key: "main" }) })
				: await fetch("/api/browser/screens", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ agent_key: "main" }) });
			if (!res.ok) throw new Error(await readDetail(res));
			const body = await res.json();
			const id = target ? body.screen_id : body.screen?.id;
			const title = target ? body.title : "";
			if (id) openScreen(id, title);
			setUrl("");
		} catch (e) {
			toast.error(t("dock.browser.openFailed", "Could not open the page"), String((e as Error).message ?? e));
		} finally {
			setBusy(false);
		}
	};

	return (
		<div className={styles.panel} data-browser-picker>
			<div className={styles.header}>
				<Globe size={14} aria-hidden /> <span className={styles.title}>{t("dock.browser.pick", "Agent screens")}</span>
				<button type="button" className={styles.iconBtn} title={t("common.refresh", "Refresh")} onClick={() => void load()}><RefreshCw size={13} /></button>
			</div>
			{!owner && <div className={styles.muted}>{t("dock.browser.notOwner", "This process does not own the browser; open the app served by the web process.")}</div>}
			{screens === null && <div className={styles.muted}>{t("common.loading", "Loading…")}</div>}
			{screens && screens.length === 0 && <div className={styles.muted}>{t("dock.browser.noScreens", "No screens open. Ask an agent to read a page, or open one below.")}</div>}
			<ul className={styles.list}>
				{(screens ?? []).map((s) => (
					<li key={s.id}>
						<button type="button" className={styles.row} onClick={() => openScreen(s.id, s.title)} aria-label={`${s.agent_key}: ${s.title || s.url || s.id}`}>
							<Globe size={14} aria-hidden />
							<span className={styles.name}>{s.title || s.url || s.id}</span>
							<span className={styles.meta}>{s.agent_key} · {statusLabel(s.status, t)}</span>
						</button>
					</li>
				))}
			</ul>
			{error && <div className={styles.error}>{error}</div>}
			<form className={styles.urlBox} onSubmit={(ev) => { ev.preventDefault(); void openUrl(); }}>
				<input
					className={styles.urlInput}
					value={url}
					onChange={(ev) => setUrl(ev.target.value)}
					placeholder={t("dock.browser.urlPlaceholder", "https://… — open in a new screen (signed in as you where allowed)")}
					aria-label={t("dock.browser.urlLabel", "Page to open")}
					disabled={busy}
				/>
				<Button type="submit" size="sm" variant="secondary" icon={<Plus size={13} />} loading={busy}>
					{url.trim() ? t("dock.browser.open", "Open") : t("dock.browser.newScreen", "New screen")}
				</Button>
			</form>
			<RecordSection />
		</div>
	);
}

function Screen({ screenId }: { screenId: string }) {
	const { t } = useTranslation();
	const { status, frame, screen, notice, send, clearNotice } = useBrowserSocket(screenId);
	const imgRef = useRef<HTMLImageElement | null>(null);
	const [url, setUrl] = useState("");
	const takenOver = screen?.status === "taken_over";
	// Bots B3: a handoff is the same live surface as a take-over (the user
	// drives, the agent is paused) with Done / Cancel instead of Resume.
	const inHandoff = screen?.status === "handoff";
	const handoff = inHandoff ? (screen?.handoff ?? null) : null;
	const userDrives = takenOver || inHandoff;

	useEffect(() => {
		if (!notice) return;
		toast.info(t("dock.browser.notice", "Browser"), notice.detail);
		clearNotice();
	}, [notice, clearNotice, t]);

	const point = (ev: React.MouseEvent | React.WheelEvent) => {
		const img = imgRef.current;
		if (!img) return null;
		const rect = img.getBoundingClientRect();
		return toViewportPoint({ x: ev.clientX, y: ev.clientY }, rect, { width: img.naturalWidth, height: img.naturalHeight });
	};

	const onClick = (ev: React.MouseEvent) => {
		if (!userDrives) return;
		const p = point(ev);
		if (p) send({ type: "input", kind: "click", x: p.x, y: p.y, button: ev.button === 2 ? "right" : "left" });
	};
	const onWheel = (ev: React.WheelEvent) => {
		if (!userDrives) return;
		const p = point(ev);
		if (p) send({ type: "input", kind: "wheel", x: p.x, y: p.y, dx: ev.deltaX, dy: ev.deltaY });
	};
	const onKeyDown = (ev: React.KeyboardEvent) => {
		if (!userDrives) return;
		if (SPECIAL_KEYS.has(ev.key) || ev.ctrlKey || ev.metaKey) {
			ev.preventDefault();
			const combo = `${ev.ctrlKey ? "Control+" : ""}${ev.metaKey ? "Meta+" : ""}${ev.shiftKey && ev.key.length > 1 ? "Shift+" : ""}${ev.key}`;
			send({ type: "input", kind: "key", key: combo });
		} else if (ev.key.length === 1) {
			ev.preventDefault();
			send({ type: "input", kind: "type", text: ev.key });
		}
	};
	const navigate = (ev: React.FormEvent) => {
		ev.preventDefault();
		if (!url.trim()) return;
		send({ type: "input", kind: "navigate", url: url.trim() });
		setUrl("");
	};

	return (
		<div className={styles.panel} data-browser-screen={screenId}>
			<div className={styles.header}>
				<Globe size={14} aria-hidden />
				<span className={styles.url} title={screen?.url}>{screen?.title || screen?.url || screenId}</span>
				<span className={`${styles.chip} ${userDrives ? styles.chipUser : ""}`} data-screen-status={screen?.status}>
					{status.state === "connecting" ? t("common.loading", "Loading…") : statusLabel(screen?.status ?? "", t)}
				</span>
				{screen?.agent_key && <span className={styles.meta}>{screen.agent_key}</span>}
				<div className={styles.controls}>
					{inHandoff ? (
						<>
							<Button size="sm" variant="primary" icon={<Check size={13} />} onClick={() => send({ type: "handoff_done" })} title={t("dock.browser.handoffDoneHint", "You finished the step — give the screen back; the agent only learns that you did")} data-handoff-done>
								{t("dock.browser.handoffDone", "Done")}
							</Button>
							<Button size="sm" variant="ghost" icon={<X size={13} />} onClick={() => send({ type: "handoff_cancel" })} title={t("dock.browser.handoffCancelHint", "Give the screen back without finishing the step")} data-handoff-cancel>
								{t("dock.browser.handoffCancel", "Cancel")}
							</Button>
						</>
					) : takenOver ? (
						<Button size="sm" variant="primary" icon={<Play size={13} />} onClick={() => send({ type: "resume" })} title={t("dock.browser.resumeHint", "Hand the screen back to the agent")}>
							{t("dock.browser.resume", "Resume")}
						</Button>
					) : (
						<Button size="sm" variant="secondary" icon={<Hand size={13} />} onClick={() => send({ type: "take_over" })} disabled={status.state !== "ready"} title={t("dock.browser.takeOverHint", "Drive the screen yourself — the agent cannot see it until you resume")}>
							{t("dock.browser.takeOver", "Take over")}
						</Button>
					)}
					<Button size="sm" variant="ghost" icon={<Square size={13} />} onClick={() => send({ type: "stop" })} disabled={status.state !== "ready"} title={t("dock.browser.stopHint", "Close this screen")}>
						{t("dock.browser.stop", "Stop")}
					</Button>
				</div>
			</div>
			{inHandoff && (
				<div className={styles.handoffBanner} role="status" data-handoff-kind={handoff?.kind}>
					<ShieldAlert size={14} aria-hidden />
					<strong>{handoffLabel(handoff?.kind ?? "", t)}</strong>
					{handoff?.host && <span className={styles.meta}>{handoff.host}</span>}
					<span className={styles.handoffHint}>{t("dock.browser.handoffHint", "Finish this step here — what you type is never recorded — then press Done. The agent only learns that you finished.")}</span>
				</div>
			)}
			{userDrives && (
				<form className={styles.urlBox} onSubmit={navigate}>
					<input className={styles.urlInput} value={url} onChange={(ev) => setUrl(ev.target.value)} placeholder={t("dock.browser.goPlaceholder", "Go to https://…")} aria-label={t("dock.browser.goLabel", "Address")} />
					<Button type="submit" size="sm" variant="secondary">{t("dock.browser.go", "Go")}</Button>
				</form>
			)}
			<div
				className={`${styles.stage} ${userDrives ? styles.stageLive : ""}`}
				role={userDrives ? "application" : undefined}
				tabIndex={userDrives ? 0 : -1}
				aria-label={userDrives ? t("dock.browser.liveLabel", "The agent's screen — you are driving it") : t("dock.browser.watchLabel", "The agent's screen")}
				onClick={onClick}
				onContextMenu={(ev) => { if (userDrives) { ev.preventDefault(); onClick(ev); } }}
				onWheel={onWheel}
				onKeyDown={onKeyDown}
			>
				{status.state === "error" && (
					<div className={styles.overlay} role="alert"><strong>{t("dock.browser.unavailable", "Screen unavailable")}</strong><div>{status.detail}</div></div>
				)}
				{status.state === "closed" && (
					<div className={styles.overlay}>{t("dock.browser.closed", "This screen is closed.")}</div>
				)}
				{frame ? (
					<img ref={imgRef} className={styles.frame} src={frameSrc(frame)} alt="" draggable={false} />
				) : (
					status.state === "ready" && <div className={styles.muted}>{t("dock.browser.waitingFrame", "Waiting for the first frame…")}</div>
				)}
				{userDrives && <div className={styles.liveBadge}>{inHandoff ? t("dock.browser.handoffBadge", "Your step — the agent is paused") : t("dock.browser.liveBadge", "You are driving — the agent is blinded")}</div>}
			</div>
		</div>
	);
}

export function BrowserPanel({ panel }: DockKindProps) {
	const { screenId } = browserData(panel);
	if (!screenId) return <Picker panel={panel} />;
	return <Screen screenId={screenId} />;
}

export { browserPanel };
